import { format, differenceInDays, isPast } from "date-fns";
import { MapPin, Calendar, ChevronRight, Link, Copy, Check, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { toast } from "sonner";
import type { CommunityEvent } from "@/types/eventPartner";

interface EventCardProps {
  event: CommunityEvent;
  onClick: () => void;
  isSelected?: boolean;
}

export const EventCard = ({ event, onClick, isSelected }: EventCardProps) => {
  const [copied, setCopied] = useState(false);
  const eventDate = new Date(event.starts_at);
  const formattedDate = format(eventDate, "MMM d, yyyy 'at' h:mm a");

  const now = new Date();
  const daysUntil = differenceInDays(eventDate, now);
  const eventPassed = isPast(eventDate);

  const slug = event.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const eventUrl = `www.discoparty.ai/${slug}`;

  const handleCopyUrl = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`https://${eventUrl}`).then(() => {
      setCopied(true);
      toast.success("Event link copied!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-3">
      <Card
        className={`cursor-pointer transition-all hover:border-primary/50 ${
          isSelected ? "border-primary ring-1 ring-primary" : ""
        }`}
        onClick={onClick}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {event.image && (
                <div className="mb-3 rounded-lg overflow-hidden h-32 bg-muted">
                  <img
                    src={event.image}
                    alt={event.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <h3 className="font-semibold text-lg text-foreground truncate">
                {event.name}
              </h3>

              <div className="flex items-center gap-1.5 mt-2 text-muted-foreground">
                <MapPin className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm truncate">{event.venue_name}</span>
              </div>

              <div className="flex items-center gap-1.5 mt-1 text-muted-foreground">
                <Calendar className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">{formattedDate}</span>
              </div>
            </div>

            <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 ml-2" />
          </div>
        </CardContent>
      </Card>

      {/* Event URL with clipboard */}
      <button
        onClick={handleCopyUrl}
        className="flex items-center gap-2 text-sm text-gray-900 hover:text-[#2BFF00] transition-colors group"
      >
        <Link className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate max-w-[200px] underline underline-offset-2">{eventUrl}</span>
        {copied ? (
          <Check className="h-3.5 w-3.5 text-[#2BFF00] flex-shrink-0" />
        ) : (
          <Copy className="h-3.5 w-3.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </button>

      {/* Countdown */}
      <div className="flex items-center gap-2 text-sm text-gray-900 hover:text-[#2BFF00] transition-colors">
        <Clock className="h-3.5 w-3.5 flex-shrink-0" />
        {eventPassed ? (
          <span>Event has passed</span>
        ) : daysUntil === 0 ? (
          <span className="font-medium text-foreground">Event is today!</span>
        ) : daysUntil === 1 ? (
          <span className="font-medium text-foreground">1 day away</span>
        ) : (
          <span><span className="font-medium text-foreground">{daysUntil}</span> days away</span>
        )}
      </div>
    </div>
  );
};
